import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Pagination } from '@/shared/components/custom/pagination/Pagination';
import { usePaginationFromUrl } from '@/shared/hooks/usePaginationFromUrl';
import { useAllTasks } from '../../hooks/useAllTasks';
import { AllTasksFilters } from './AllTasksFilters';
import { AllTasksTableRow } from './AllTasksTableRow';
import type { AllTasksParams } from '../../types';

export function AllTasksTable() {
  const { t } = useTranslation('analytics');

  const [filters, setFilters] = useState<Omit<AllTasksParams, 'page' | 'pageSize'>>({});

  const { data: totalData } = useAllTasks({ ...filters, page: 1, pageSize: 1 });
  const pagination = usePaginationFromUrl({ totalItems: totalData?.totalCount ?? 0 });

  const params: AllTasksParams = {
    ...filters,
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
  };

  const { data, isLoading } = useAllTasks(params);
  const items = data?.items ?? [];

  const handleFiltersChange = (updated: AllTasksParams) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { page, pageSize, ...rest } = updated;
    setFilters(rest);
    pagination.first();
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-8 w-full max-w-sm" />
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <AllTasksFilters
        filters={filters}
        onChange={handleFiltersChange}
        totalCount={data?.totalCount}
      />

      {!items.length ? (
        <p className="py-8 text-center text-sm text-muted-foreground">{t('empty.allTasks')}</p>
      ) : (
        <div className="overflow-x-auto" data-testid="analytics-all-tasks-table">
          <table className="min-w-full table-auto">
            <thead>
              <tr className="border-b text-xs text-muted-foreground">
                <th className="py-2 pl-4 pr-2 text-left font-medium">
                  {t('allTasks.columns.task')}
                </th>
                <th className="px-2 py-2 text-left font-medium">{t('allTasks.columns.project')}</th>
                <th className="px-2 py-2 text-left font-medium">
                  {t('allTasks.columns.assignee')}
                </th>
                <th className="px-2 py-2 text-left font-medium">{t('allTasks.columns.status')}</th>
                <th className="px-2 py-2 text-left font-medium">
                  {t('allTasks.columns.priority')}
                </th>
                <th className="px-2 py-2 text-left font-medium">{t('allTasks.columns.dueDate')}</th>
                <th className="px-2 py-2 text-right font-medium">
                  {t('allTasks.columns.estimated')}
                </th>
                <th className="px-2 py-2 text-right font-medium">
                  {t('allTasks.columns.tracked')}
                </th>
                <th className="px-2 py-2 pr-4 text-right font-medium">
                  {t('allTasks.columns.efficiency')}
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((task) => (
                <AllTasksTableRow key={task.taskId ?? task.title} task={task} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(data?.totalCount ?? 0) > 0 && (
        <div data-testid="analytics-all-tasks-pagination">
          <Pagination {...pagination} />
        </div>
      )}
    </div>
  );
}
